import { useId, useRef, useState } from 'react';
import { Controller, useForm, useWatch, type DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Form, Input, Label, Modal, Select } from '@shared/components';
import { useAddAuthorisedContactMutation } from '../../../hooks/useAddAuthorisedContactMutation';
import {
    authorisedContactSchema,
    toRelationshipValue,
    type AuthorisedContactFormInput,
    type AuthorisedContactFormValues,
} from '../../../schemas/authorisedContact.schema';
import { RELATIONSHIP_OPTIONS } from '../../../types/authorisedContacts.types';
import { RELATIONSHIP_LABEL } from '../../../utils/authorisedContactLabels';
import { mapContactSubmitError } from '../../../utils/mapContactSubmitError';
import { FieldError } from './FieldError';

interface Props {
    agreementId: string;
    isOpen: boolean;
    onClose: () => void;
}

const RELATIONSHIP_SELECT_OPTIONS = RELATIONSHIP_OPTIONS.map((value) => ({
    value,
    label: RELATIONSHIP_LABEL[value],
}));

const EMPTY_FORM: DefaultValues<AuthorisedContactFormInput> = { name: '', relationshipOther: '' };

export function AddAuthorisedContactDialog({ agreementId, isOpen, onClose }: Props) {
    const headingId = useId();
    const ids = {
        name: `${headingId}-name`,
        relationship: `${headingId}-relationship`,
        relationshipOther: `${headingId}-relationship-other`,
    };
    const nameWrapperRef = useRef<HTMLSpanElement>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const mutation = useAddAuthorisedContactMutation(agreementId);

    const {
        control,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<AuthorisedContactFormInput, unknown, AuthorisedContactFormValues>({
        resolver: zodResolver(authorisedContactSchema),
        mode: 'onBlur',
        defaultValues: EMPTY_FORM,
    });

    const relationship = useWatch({ control, name: 'relationship' });

    const handleClose = () => {
        if (mutation.isPending) return;
        setFormError(null);
        clearErrors();
        onClose();
    };

    const handleValid = (values: AuthorisedContactFormValues) => {
        setFormError(null);
        mutation.mutate(
            { name: values.name, relationship: toRelationshipValue(values) },
            {
                onSuccess: () => {
                    reset(EMPTY_FORM);
                    onClose();
                },
                onError: (error: unknown) => {
                    const { fieldErrors, message } = mapContactSubmitError(error);
                    fieldErrors.forEach(({ field, message: fieldMessage }) => setError(field, { message: fieldMessage }));
                    setFormError(message);
                },
            },
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            showCloseButton={!mutation.isPending}
            ariaLabelledBy={headingId}
            className="max-w-lg p-6"
            initialFocusRef={nameWrapperRef}
        >
            <h2 id={headingId} className="mb-4 text-lg font-semibold">
                Add authorised contact
            </h2>
            <Form onSubmit={handleSubmit(handleValid)} noValidate className="space-y-4">
                {formError && <Alert variant="error">{formError}</Alert>}
                <div>
                    <Label htmlFor={ids.name}>Name</Label>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <span ref={nameWrapperRef}>
                                <Input
                                    id={ids.name}
                                    value={field.value ?? ''}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    error={!!errors.name}
                                    aria-invalid={!!errors.name}
                                    aria-describedby={errors.name ? `${ids.name}-error` : undefined}
                                    disabled={mutation.isPending}
                                />
                            </span>
                        )}
                    />
                    <FieldError id={`${ids.name}-error`} message={errors.name?.message} />
                </div>

                <div>
                    <Label htmlFor={ids.relationship}>Relationship to the plan holder</Label>
                    <Controller
                        name="relationship"
                        control={control}
                        render={({ field }) => (
                            <Select
                                id={ids.relationship}
                                ariaLabel="Relationship to the plan holder"
                                placeholder="Choose the relationship"
                                options={RELATIONSHIP_SELECT_OPTIONS}
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={!!errors.relationship}
                                ariaDescribedBy={errors.relationship ? `${ids.relationship}-error` : undefined}
                                disabled={mutation.isPending}
                            />
                        )}
                    />
                    <FieldError id={`${ids.relationship}-error`} message={errors.relationship?.message} />
                </div>

                {relationship === 'other' && (
                    <div>
                        <Label htmlFor={ids.relationshipOther}>Describe the relationship</Label>
                        <Controller
                            name="relationshipOther"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    id={ids.relationshipOther}
                                    value={field.value ?? ''}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    error={!!errors.relationshipOther}
                                    aria-invalid={!!errors.relationshipOther}
                                    aria-describedby={errors.relationshipOther ? `${ids.relationshipOther}-error` : undefined}
                                    disabled={mutation.isPending}
                                />
                            )}
                        />
                        <FieldError id={`${ids.relationshipOther}-error`} message={errors.relationshipOther?.message} />
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <Button type="button" variant="clear" disabled={mutation.isPending} onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={mutation.isPending}>
                        {mutation.isPending ? 'Saving…' : 'Save contact'}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
}
